import { useState } from "react";
import { Alert, Button, Select, Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { IconArrowLeft, IconArrowRight, IconCalendar, IconClock, IconPhone, IconWallet } from "@tabler/icons-react";
import useIsSmall from "@/hooks/useIsSmall";
import { useNavigate, useSearchParams } from "react-router-dom";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import merchantUserService from "@/services/merchantUser";
import { BarberDashboardFilter, getBarberOverview } from "@/services/barberDashboard";
import { dateRange, formatLocalDate, localDate } from "@/services/barberDashboard/utils";
import classes from "./report.module.scss";

const number = (value: number) => value.toLocaleString("fa-IR", { maximumFractionDigits: 1 });
const money = (value: number) => `${number(value)} ریال`;
const shortMoney = (value: number) => number(value / 1_000_000);
const shortDate = (value: string) => localDate(value).toLocaleDateString("fa-IR", { day: "numeric", month: "short" });
const ranges = [{ value: "7", label: "۷ روز" }, { value: "default", label: "۳۰ روز" }, { value: "90", label: "۹۰ روز" }];

function Change({ value }: { value: number | null }) {
  if (value === null) return <span className={classes.muted}>بدون مبنای مقایسه</span>;
  return <span className={value < 0 ? classes.decrease : classes.increase}>
    {number(Math.abs(value))}٪ {value < 0 ? "کمتر" : "بیشتر"} از دوره قبل
  </span>;
}

export default function BarberOverview() {
  const { isManager, isResolved } = useDashboardAccess();
  const navigate = useNavigate();
  const isSmall = useIsSmall();
  const [searchParams, setSearchParams] = useSearchParams();
  const memberParam = searchParams.get("merchantUserId");
  const member = memberParam && /^\d+$/.test(memberParam) && Number.isSafeInteger(Number(memberParam)) && Number(memberParam) > 0
    ? String(Number(memberParam)) : null;
  const setMember = (value: string | null) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (value) next.set("merchantUserId", value);
      else next.delete("merchantUserId");
      return next;
    });
  };
  const [range, setRange] = useState<BarberDashboardFilter>({});
  const [days, setDays] = useState("default");
  const staff = useQuery({ queryKey: ["getMerchantUsers"], queryFn: merchantUserService.getMerchantUsers, enabled: isManager });
  const filter = { ...range, ...(isManager && member ? { merchantUserId: Number(member) } : {}) };
  const query = useQuery({ queryKey: ["barber-dashboard", filter], queryFn: () => getBarberOverview(filter), enabled: isResolved, retry: false });
  const data = query.data;
  const next = data?.headline.nextAppointment;
  const services = data?.topServices.slice(0, 3) ?? [];
  const maxEarnings = Math.max(1, ...services.map(service => service.earnings));
  // The API end is exclusive; show the last included day in the human-readable range.
  const periodEnd = data ? localDate(data.period.to) : null;
  periodEnd?.setDate(periodEnd.getDate() - 1);

  const title = data ? data.barber.isSelf ? "گزارش من" : `گزارش ${data.barber.fullName}` : isManager ? "آمار تیم" : "گزارش من";

  return <main className={classes.page} dir="rtl">
    {/* Phones: a title bar with a way back, like the other inner pages. */}
    {isSmall && <div className={classes.mobileBar}>
      <button type="button" className={classes.back} aria-label="بازگشت" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}><IconArrowRight size={22} /></button>
      <strong>{title}</strong>
    </div>}
    <div className={classes.content}>
      <header className={classes.header}>
        <div>
          <span className={classes.eyebrow}>خلاصه عملکرد</span>
          <h1>{data ? data.barber.isSelf ? "گزارش من" : `گزارش ${data.barber.fullName}` : "گزارش عملکرد"}</h1>
          <p>{data && periodEnd ? `${formatLocalDate(data.period.from)} تا ${periodEnd.toLocaleDateString("fa-IR")}` : "یک نگاه به درآمد و برنامه کاری"}</p>
        </div>
        <div className={classes.filters}>
          {isManager && <Select aria-label="عضو تیم" placeholder="گزارش خودم" clearable searchable value={member} onChange={setMember}
            data={(staff.data ?? []).filter(m => m.id != null && !(m as { isChairRenter?: boolean }).isChairRenter).map(m => ({ value: String(m.id), label: `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim() || `عضو ${m.id}` }))} />}
          <div className={classes.range} aria-label="بازه گزارش">
            {ranges.map(item => <button key={item.value} type="button" aria-pressed={days === item.value}
              onClick={() => { setDays(item.value); setRange(item.value === "default" ? {} : dateRange(Number(item.value))); }}>{item.label}</button>)}
          </div>
        </div>
      </header>

      {staff.isError && isManager && <Alert color="red">فهرست تیم دریافت نشد. <Button variant="subtle" onClick={() => void staff.refetch()}>تلاش دوباره</Button></Alert>}
      {query.isLoading && <div className={classes.loading} aria-label="در حال دریافت گزارش"><Skeleton height={165} radius="lg" /><Skeleton height={165} radius="lg" /><Skeleton height={165} radius="lg" /><Skeleton height={320} radius="lg" /></div>}
      {query.isError && <Alert color="red">{(query.error as { response?: { data?: { message?: string } } })?.response?.data?.message || "دریافت گزارش انجام نشد"}<Button variant="subtle" onClick={() => void query.refetch()}>تلاش دوباره</Button></Alert>}

      {data && <>
        <section className={classes.metrics} aria-label="شاخص‌های اصلی">
          <article className={`${classes.metric} ${classes.featured}`}>
            <div className={classes.metricLabel}><span>کارکرد این دوره</span><span className={classes.pill}>{number(data.period.days)} روز</span></div>
            <div className={classes.amount}>{number(data.earnings.registeredWork.value)}<small>ریال</small></div>
            <Change value={data.earnings.registeredWork.changePercent} />
          </article>
          <article className={classes.metric}>
            <div className={classes.metricLabel}><span>طلب از سالن</span><IconWallet size={21} stroke={1.5} /></div>
            <div className={classes.amount}>{number(data.earnings.outstanding)}<small>ریال</small></div>
            <span className={classes.muted}>مانده تا امروز · کل دوره‌ها</span>
          </article>
          <article className={classes.metric}>
            <div className={classes.metricLabel}><span>نوبت‌های امروز</span><IconCalendar size={21} stroke={1.5} /></div>
            <div className={classes.amount}>{number(data.headline.todayAppointments.total)}<small>نوبت</small></div>
            <span className={classes.muted}>{number(data.headline.todayAppointments.completed)} انجام‌شده <span className={classes.dot}>·</span> {number(data.headline.todayAppointments.remaining)} باقی‌مانده</span>
          </article>
        </section>

        <div className={classes.mainGrid}>
          <section className={classes.panel}>
            <div className={classes.panelHeading}><div><h2>روند کارکرد</h2><p>سهم شما از خدمات این دوره</p></div><span className={classes.unit}>میلیون ریال</span></div>
            <div className={classes.chart}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.earningsTrend} margin={{ top: 15, right: 12, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="barber-report-income" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7460db" stopOpacity={0.24} /><stop offset="100%" stopColor="#7460db" stopOpacity={0.01} /></linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="#eeedf3" strokeDasharray="4 5" />
                  <XAxis dataKey="date" tickFormatter={shortDate} axisLine={false} tickLine={false} minTickGap={42} tick={{ fontSize: 11, fill: "#8b8799" }} dy={10} />
                  <YAxis tickFormatter={shortMoney} axisLine={false} tickLine={false} width={42} tick={{ fontSize: 11, fill: "#8b8799" }} />
                  <Tooltip labelFormatter={value => formatLocalDate(String(value))} formatter={(value: number) => [money(value), "کارکرد"]} contentStyle={{ borderRadius: 12, border: "1px solid #eee", direction: "rtl", fontSize: 12 }} />
                  <Area type="linear" dataKey="total" stroke="#7962dc" strokeWidth={2.5} fill="url(#barber-report-income)" activeDot={{ r: 5, strokeWidth: 3, stroke: "white" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className={classes.chartSummary}>
              <div><span><i className={classes.paidDot} />وصول‌شده و قابل تسویه</span><strong>{money(data.earnings.settleableEarnings)}</strong></div>
              <div><span><i className={classes.pendingDot} />در انتظار وصول</span><strong>{money(data.earnings.registeredWork.pending)}</strong></div>
            </div>
          </section>

          <section className={`${classes.panel} ${classes.appointment}`}>
            <div className={classes.panelHeading}><h2>نوبت بعدی</h2><span className={classes.icon}><IconClock size={20} stroke={1.5} /></span></div>
            {next ? <>
              <div className={classes.nextTime}><strong>{next.startTime.slice(0, 5)}</strong><span>{formatLocalDate(next.appointmentDate)}</span></div>
              <div className={classes.customer}><span className={classes.avatar}>{next.customerFullName?.trim().charAt(0) || "م"}</span><div><h3>{next.customerFullName || "مشتری بدون نام"}</h3><p>{next.serviceTitle || "خدمت ثبت‌شده"}</p></div></div>
              <div className={classes.appointmentMeta}><span>{number(next.durationMinutes)} دقیقه</span><span>تا ساعت {next.endTime.slice(0, 5)}</span></div>
              <div className={classes.appointmentActions}><Button variant="light" color="violet" fullWidth rightSection={<IconArrowLeft size={16} />} onClick={() => navigate("/dashboard/calendar")}>مشاهده تقویم</Button>{next.customerPhoneNumber && <a className={classes.call} href={`tel:${next.customerPhoneNumber}`} aria-label="تماس با مشتری"><IconPhone size={19} /></a>}</div>
            </> : <div className={classes.emptyAppointment}><IconCalendar size={38} stroke={1.2} /><h3>نوبتی در پیش نیست</h3><p>برنامهٔ بعدی شما اینجا نمایش داده می‌شود.</p><Button variant="light" color="violet" onClick={() => navigate("/dashboard/calendar")}>مشاهده تقویم</Button></div>}
          </section>
        </div>

        <div className={classes.bottomGrid}>
          <section className={classes.panel}>
            <div className={classes.panelHeading}><h2>مشتریان این دوره</h2><span className={classes.unit}>{number(data.period.days)} روز</span></div>
            <div className={classes.customers}>
              <div><strong>{number(data.customers.new.value)}</strong><span>مشتری جدید برای شما</span></div>
              <div><strong>{number(data.customers.returning.value)}</strong><span>مشتری برگشتی</span></div>
              <div><strong>{data.customers.retentionPercent === null ? "—" : `${number(data.customers.retentionPercent)}٪`}</strong><span>نرخ بازگشت</span></div>
            </div>
            <div className={classes.customerFooter}><span>{number(data.customers.total)} مشتری در مجموع</span><button type="button" onClick={() => navigate("/dashboard/customers")}>فهرست مشتریان <IconArrowLeft size={15} /></button></div>
          </section>
          <section className={classes.panel}>
            <div className={classes.panelHeading}><h2>خدمات پردرآمد</h2><span className={classes.unit}>۳ خدمت برتر این دوره</span></div>
            {services.length ? <div className={classes.services}>{services.map(service => <div className={classes.service} key={service.merchantServiceId}>
              <div><span>{service.title}</span><strong>{money(service.earnings)}</strong></div>
              <div className={classes.serviceTrack}><span style={{ width: `${Math.max(0, service.earnings / maxEarnings * 100)}%` }} /></div>
            </div>)}</div> : <p className={classes.empty}>هنوز خدمتی در این دوره ثبت نشده است.</p>}
          </section>
        </div>
      </>}
    </div>
  </main>;
}
